import { featureCollection, polygon as turfPolygon } from '@turf/turf';
import { keyBy, sortBy } from 'lodash';
import { Feature, FeatureCollection, Geometry, Position } from 'geojson';
import { parse as parseWkt } from 'wellknown';
import { XMLParser } from 'fast-xml-parser';
import { generateGeojsonFeatureId } from './layers';
import { SOIL_BOUNDARIES_LAYER_ID } from '../constants/map';
import { geometryTypes } from '../constants/fields';
import { notifyMonitoring } from './monitoring';
import { createAoi, QUERY_SDM, sendSdmPostRest } from './soilDataAccess';

const GET_MAP_UNIT_NAMES = {
  service: 'interpretation',
  request: 'getrating',
  attributekey: '94', // Map unit names and symbols
  format: 'short',
};
const GET_MAP_UNIT_DESCRIPTIONS = {
  service: 'report',
  request: 'getreport',
  reportid: '666', // Map unit descriptions
  format: 'short',
};

type DescriptionMap = { [key: string]: string };
type SoilMap = { [key: string]: { legendText: string; rgbString: string } };

// This step is needed in order to generate the map legend colors for the soil
// types in the AOI, but we do not use the interpretation result id returned
// from this query.
const getMapUnitNames = (aoiid: Nullable<string>) => {
  if (!aoiid) return Promise.resolve();
  const body = { ...GET_MAP_UNIT_NAMES, aoiid };
  return sendSdmPostRest(body);
};

const getAoiSoilDescriptions = (
  aoiid: Nullable<string>,
): Promise<DescriptionMap> => {
  if (!aoiid) return Promise.resolve({});
  const body = {
    ...GET_MAP_UNIT_DESCRIPTIONS,
    aoiid,
  };

  return sendSdmPostRest(body, {}, false).then((xml) => {
    const parser = new XMLParser();
    const parsedXml = parser.parse(xml);
    const surveyAreas = Array.isArray(parsedXml.section.section)
      ? parsedXml.section.section
      : [parsedXml.section.section];
    const mapUnitDescriptions = surveyAreas.flatMap(
      (surveyArea: { section: object }) => {
        const soils = Array.isArray(surveyArea.section)
          ? surveyArea.section
          : [surveyArea.section];

        return soils.map((soil: { title: string }) => [
          soil.title,
          // Keep the survey area title with each soil description for display
          { section: { ...surveyArea, section: soil } },
        ]);
      },
    );
    return Object.fromEntries(mapUnitDescriptions);
  });
};

const generateSoilColors = (aoiid: Nullable<string>): Promise<SoilMap> => {
  if (!aoiid) return Promise.resolve({});
  const body = {
    ...QUERY_SDM,
    // eslint-disable-next-line no-useless-escape
    query: `SELECT * FROM SDA_Get_AoiSoilThematicMapLegend_By_AoiId(\'${aoiid}\')`,
  };

  return sendSdmPostRest(body, {})
    .then(({ Table }: { Table: string[][] }) => Table)
    .then((soils) =>
      soils.map(([, , , legendText, rgbString]) => ({
        legendText,
        rgbString,
      })),
    )
    .then((soils) => keyBy(soils, 'legendText'));
};

const getAoiPolygons = (aoiid: Nullable<string>) => {
  if (!aoiid) return Promise.resolve([]);
  const body = {
    ...QUERY_SDM,
    // eslint-disable-next-line no-useless-escape
    query: `SELECT * FROM SDA_Get_AoiSoilMapunitPolygon_By_AoiId(\'${aoiid}\')`,
  };

  return sendSdmPostRest(body, []).then(
    ({ Table }: { Table: string[][] }) => Table,
  );
};

const getGeometryCoordinates = (geometry: Nullable<Geometry>) => {
  switch (geometry?.type) {
    case geometryTypes.POLYGON:
      return [geometry.coordinates];
    case geometryTypes.MULTI_POLYGON:
      return geometry.coordinates;
    default:
      return null;
  }
};

const transformPolygons = (
  polygons: string[][],
  descriptions: DescriptionMap,
  soils: SoilMap,
): Feature[] => {
  return polygons.flatMap((rawPolygon): Feature[] => {
    const [
      ,
      ,
      ,
      polygonGeo,
      ,
      areaAcres,
      areaSymbol,
      mapUnitKey,
      mapUnitName,
      mapUnitSymbol,
      muPolygonKey,
      nationalMapUnitSymbol,
    ] = rawPolygon;
    // Parse well-known text and get a list of polygon coordinates
    const geometry = parseWkt(polygonGeo);
    const geometryCoords = getGeometryCoordinates(geometry);
    if (!geometryCoords) {
      notifyMonitoring(`Unable to parse soil boundary: ${polygonGeo}`);
      return [];
    }

    return geometryCoords.map((coordinates: Position[][]) =>
      turfPolygon(coordinates, {
        areaAcres,
        areaSymbol,
        description: descriptions[`${mapUnitSymbol}--${mapUnitName}` || ''],
        mapUnitKey,
        mapUnitName,
        mapUnitSymbol,
        muPolygonKey,
        name: mapUnitName,
        nationalMapUnitSymbol,
        rgbString: soils[mapUnitName || '']?.rgbString,
      }),
    );
  });
};

// Load the soil boundaries from the Soil Data Mart (SDM). We define an area of
// interest (AOI) for the given FeatureCollection and query for an array of soil
// polygons contained in the AOI, along with a map legend containing colors for
// each soil type.
// See: https://www.notion.so/propagateventures/Suitability-Soil-Layer-1431a5aad514805483e0c65cfd886d33
const loadSoilBoundaries = async (boundingFeatures: FeatureCollection) => {
  const features = await createAoi(boundingFeatures).then((aoiid) =>
    getMapUnitNames(aoiid).then(() =>
      getAoiSoilDescriptions(aoiid).then((descriptions) =>
        generateSoilColors(aoiid).then((soils) =>
          getAoiPolygons(aoiid).then((polygons) =>
            transformPolygons(polygons, descriptions, soils),
          ),
        ),
      ),
    ),
  );

  // Add feature id and sort by name for display in left menu
  return featureCollection(
    sortBy(
      features.map((feature) => {
        const id = generateGeojsonFeatureId(SOIL_BOUNDARIES_LAYER_ID);
        return {
          ...feature,
          id,
          properties: { ...feature.properties, id },
          type: 'Feature',
        };
      }),
      'properties.name',
    ),
  );
};

export default loadSoilBoundaries;
